/** localStorage/sessionStorage pueden no existir o lanzar (modo privado, cookies bloqueadas): nunca romper. */

function read<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: unknown): void {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // Sin almacenamiento, el widget funciona igual (solo no recuerda).
  }
}

export interface ReporterMemory {
  name?: string;
  email?: string;
}

export const loadReporter = () => read<ReporterMemory>(() => localStorage, "anota:reporter") ?? {};
export const saveReporter = (r: ReporterMemory) => write(() => localStorage, "anota:reporter", r);

/** La configuración del proyecto se guarda unos minutos: cambiar de página no repite la petición. */
const CONFIG_TTL = 5 * 60_000;

// En localStorage: vale para todas las páginas y visitas de la web (una petición cada 5 minutos, no una por página).
export function cachedConfig<T>(key: string): T | null {
  const hit = read<{ at: number; value: T }>(() => localStorage, `anota:cfg:${key}`);
  return hit && Date.now() - hit.at < CONFIG_TTL ? hit.value : null;
}

export const cacheConfig = (key: string, value: unknown) => write(() => localStorage, `anota:cfg:${key}`, { at: Date.now(), value });

/**
 * «Solo con enlace» y este visitante no tiene uno: se recuerda una hora, para no preguntar en cada página (en
 * una web con muchas visitas, eso agotaría las peticiones diarias del plan gratis).
 */
const DENIED_TTL = 60 * 60_000;
export const isDenied = (key: string) => {
  const at = read<number>(() => localStorage, `anota:no:${key}`);
  return typeof at === "number" && Date.now() - at < DENIED_TTL;
};
export const rememberDenied = (key: string) => write(() => localStorage, `anota:no:${key}`, Date.now());


/** "Ocultar la barra" (estilo barra de revisión) dura lo que la pestaña del navegador: luego queda la pestaña lateral. */
export const isBarHidden = () => read<boolean>(() => sessionStorage, "anota:bar-hidden") === true;
export const hideBar = () => write(() => sessionStorage, "anota:bar-hidden", true);

/** "Ocultar pines" (quien revisa) dura lo que la pestaña del navegador. */
export const arePinsHidden = () => read<boolean>(() => sessionStorage, "anota:pins-hidden") === true;
export const setPinsHidden = (hidden: boolean) => write(() => sessionStorage, "anota:pins-hidden", hidden);

/**
 * «Tus notas»: los accesos firmados a las notas que se enviaron desde este navegador (`<id>.<caducidad>.<firma>`),
 * los más nuevos primero y como mucho 50 por proyecto. Sin cuenta: quien comparte el navegador, las ve.
 */
const MINE_MAX = 50;
/** Los que siguen valiendo (la caducidad, en segundos, va en el propio acceso). */
export const mineTokens = (key: string) =>
  (read<string[]>(() => localStorage, `anota:mine:${key}`) ?? []).filter((t) => Number(t.split(".")[1]) * 1000 > Date.now());
export const rememberMine = (key: string, token: string) =>
  write(() => localStorage, `anota:mine:${key}`, [token, ...mineTokens(key).filter((t) => t !== token)].slice(0, MINE_MAX));

/**
 * La clave «mis notas» de este navegador en el proyecto (`mn_` + 24 letras y números al azar): va con cada nota y
 * es el enlace para verlas en otro dispositivo. Sin almacenamiento, una nueva cada vez (y el enlace no sirve).
 */
export function mineKey(key: string): string {
  const saved = read<string>(() => localStorage, `anota:clave:${key}`);
  if (saved && /^mn_[A-Za-z0-9]{24}$/.test(saved)) return saved;
  const abc = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  // 248 = 62 × 4: sin sesgo al pasar cada byte a una de las 62 letras.
  const out: string[] = [];
  while (out.length < 24) for (const b of crypto.getRandomValues(new Uint8Array(32))) if (b < 248 && out.length < 24) out.push(abc[b % 62]!);
  const created = `mn_${out.join("")}`;
  write(() => localStorage, `anota:clave:${key}`, created);
  return created;
}

/** Lo último que vio de cada nota (estado y respuestas del equipo): lo que cambió desde entonces es una novedad. */
const SEEN_MAX = 100;
export const seenNotes = (key: string) => read<Record<string, string>>(() => localStorage, `anota:seen:${key}`) ?? {};
export function markSeen(key: string, id: string, signature: string): void {
  markAllSeen(key, [[id, signature]]);
}
/** Varias a la vez (descartar el aviso, cerrar «Tus notas»): una sola escritura. */
export function markAllSeen(key: string, seen: [id: string, signature: string][]): void {
  if (!seen.length) return;
  const ids = new Set(seen.map(([id]) => id));
  const rest = Object.entries(seenNotes(key)).filter(([k]) => !ids.has(k));
  write(() => localStorage, `anota:seen:${key}`, Object.fromEntries([...rest, ...seen].slice(-SEEN_MAX)));
}
